import { useCallback, useEffect, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'

/**
 * Hook responsible for managing request to prevent race conditions
 * Single Responsibility: Request lifecycle and cancellation management=
 * @returns {Object} - Functions to start a new request, check if current request, and check if component is mounted
 * @property {Function} startNewRequest - Starts a new request and returns its ID
 * @property {Function} isCurrentRequest - Checks if the given request ID is the current one
 * @property {Function} isMounted - Checks if the component is still mounted
 */
export const useRequestLifeCycleHandler = () => {
    const isMountedRef = useRef(true)
    const currentRequestIdRef = useRef<string | null>(null)

    useEffect(() => {
        return () => {
            isMountedRef.current = false
            currentRequestIdRef.current = null
        }
    }, [])

    const isCurrentRequest = useCallback((requestId: string) => {
        return isMountedRef.current && currentRequestIdRef.current === requestId
    }, [])

    const startNewRequest = useCallback(() => {
        const requestId = uuidv4()
        currentRequestIdRef.current = requestId
        return requestId
    }, [])

    return {
        startNewRequest,
        isCurrentRequest,
        isMounted: () => isMountedRef.current,
    }
}
